import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, filter } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis
} from 'ng-apexcharts';
import { RestService, CoachReminder, DashboardMilestone, DashboardStatistics, LeaderboardResponse, TestPerformance, TrainNextRecommendation } from '../service/rest/rest.service';
import { UserMilestoneBadgesService } from '../service/user-milestone-badges.service';
import { AuthService } from '../service/auth/auth.service';
import { ContentPackService } from '../service/content-pack/content-pack.service';
import { LoggerService } from '../service/utils/logger.service';
import { SafeHtmlPipe } from '../shared/safe-html.pipe';
import { SkyCoachPanelComponent } from '../sky-coach/sky-coach-panel.component';
import { SkyCoachContext } from '../service/sky/sky-coach-state.service';
import { RetentionFeatureFlagsService } from '../service/retention/retention-feature-flags.service';
import { ConnectivityService } from '../service/connectivity/connectivity.service';
import { BrandService } from '../service/brand/brand.service';
import { BRAND } from '../service/brand/brand';
import {
  DASHBOARD_COURSE_STORAGE_SUFFIX,
  parseStoredCourseId,
  pickPreferredCourseId,
  PlanningCourse,
} from './dashboard-course-preference';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  responsive: ApexResponsive[];
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  legend: ApexLegend;
  fill: ApexFill;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  grid: ApexGrid;
  markers: ApexMarkers;
};

const DASHBOARD_SUBJECT_ABBREVIATIONS: Record<string, string> = {
  '010': 'Air Law',
  '021': 'Airframe & Sys.',
  '022': 'Instruments',
  '031': 'Mass & Balance',
  '032': 'Performance',
  '033': 'Flight Planning',
  '040': 'Human Performance',
  '050': 'Meteorology',
  '061': 'General Nav.',
  '062': 'Radio Nav.',
  '070': 'Ops Procedures',
  '081': 'Principles of Flight',
  '091': 'VFR Comms',
  '092': 'IFR Comms',
};

export function abbreviateDashboardSubject(value?: string | null): string {
  const subject = value?.trim();
  if (!subject) {
    return 'Course setup';
  }

  const codeMatch = subject.match(/^(\d{3})\s*[-–]\s*(.+)$/);
  if (codeMatch) {
    const [, code] = codeMatch;
    const abbreviation = DASHBOARD_SUBJECT_ABBREVIATIONS[code];
    if (abbreviation) {
      return `${code} - ${abbreviation}`;
    }
  }

  return subject
    .replace(/Airframe,\s*Systems,\s*Electrics(?:,\s*and)?\s*Power Plant/i, 'Airframe & Sys.')
    .replace(/Flight Planning and Monitoring/i, 'Flight Planning')
    .replace(/Human Performance and Limitations/i, 'Human Performance')
    .replace(/Operational Procedures/i, 'Ops Procedures')
    .replace(/Communications/gi, 'Comms')
    .replace(/Navigation/gi, 'Nav.');
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    CommonModule,
    FormsModule,
    NgApexchartsModule,
    SafeHtmlPipe,
    SkyCoachPanelComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit, OnDestroy {
  averageScore: number = 0;
  questionsSeen: number = 0;
  testsDone: number = 0;
  xpTotal: number = 0;
  xpToday: number = 0;
  xpWeek: number = 0;
  xpLevel: number = 1;
  recentMilestones: DashboardMilestone[] = [];
  nextMilestone: DashboardMilestone | null = null;
  newsItems: { id: number; headline: string; text: string; createdAt: string }[] = [];
  newsLoading = false;
  trainNext: TrainNextRecommendation | null = null;
  coachReminder: CoachReminder | null = null;
  trainNextLoading = true;
  coachReminderLoading = true;
  goalSaving = false;
  canStartNewTest = true;
  newTestBlockedReason = '';
  hasExpiredTrial = false;
  selectedGoalPresetId: string | null = null;
  customGoalDate = '';
  coverageHelpOpen = false;
  socialBadgeCount = 0;
  leaderboardOpen = false;
  leaderboardLoading = false;
  leaderboard: LeaderboardResponse | null = null;
  planningCourses: PlanningCourse[] = [];
  currentCourseId: number | null = null;
  private lastActiveCourseId: number | null = null;
  /** ApexCharts line series need at least one point; hide chart until there is history. */
  chartHasData = false;
  chartOptions: Partial<ChartOptions> = {};
  private testHistory: TestPerformance[] = [];
  private testDataMap: Map<string, TestPerformance> = new Map(); // Map for quick lookup: "timestamp-mode" -> testData
  private routerSub?: Subscription;
  private visibilityHandler = () => {
    if (document.visibilityState === 'visible' && this.isDashboardUrl(this.router.url)) {
      void this.refreshDashboardData();
    }
  };
  private refreshPromise: Promise<void> | null = null;

  protected readonly retentionFlags = inject(RetentionFeatureFlagsService);
  protected readonly brand = inject(BrandService);
  protected readonly abbreviateSubject = abbreviateDashboardSubject;

  constructor(
    private restService: RestService,
    private authService: AuthService,
    private contentPackService: ContentPackService,
    private router: Router,
    private route: ActivatedRoute,
    private milestoneBadgesService: UserMilestoneBadgesService,
    private connectivity: ConnectivityService,
  ) {}

  async ngOnInit() {
    await this.refreshDashboardData();
    await this.loadNews();
    void this.checkContentPackUpdates();
    void this.handleLeaderboardTrigger();
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (this.isDashboardUrl(event.urlAfterRedirects)) {
          void this.refreshDashboardData();
        }
      });
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  private async refreshDashboardData(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    this.refreshPromise = Promise.all([
      this.loadFeatureFlags(),
      this.loadNewTestAccessState(),
      this.loadStatistics(),
      this.loadSocialSummary(),
    ]).then(() => this.loadTrainNext().then(() => this.loadCoachReminder()))
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }

  private isDashboardUrl(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    return path === '/dashboard';
  }

  private async loadTrainNext(): Promise<void> {
    this.trainNextLoading = true;
    this.coachReminderLoading = true;
    try {
      const sessionId = this.authService.getUser()?.sid;
      if (!sessionId) {
        this.trainNext = null;
        return;
      }
      const courses = await this.restService.getCoursePlanningOptions(sessionId);
      this.planningCourses = (courses ?? [])
        .map((course) => ({
          id: Number(course.id),
          name: String(course.name ?? `Course #${course.id}`),
        }))
        .filter((course) => Number.isFinite(course.id) && course.id > 0);
      let courseId = pickPreferredCourseId(this.planningCourses, {
        storedId: parseStoredCourseId(localStorage.getItem(this.brand.storageKey(DASHBOARD_COURSE_STORAGE_SUFFIX))),
        lastActiveCourseId: this.lastActiveCourseId,
        sessionCourseId: Number(this.restService.courseId) || null,
      });
      if (!Number.isFinite(courseId) || (courseId ?? 0) <= 0) {
        courseId = Number(await this.restService.getOfflineDefaultCourseId());
      }
      if (!Number.isFinite(courseId) || (courseId ?? 0) <= 0) {
        this.trainNext = null;
        this.currentCourseId = null;
        return;
      }
      const selectedId = courseId as number;
      this.currentCourseId = selectedId;
      this.restService.courseId = selectedId;
      this.trainNext = await this.restService.getTrainNextRecommendation(sessionId, selectedId);
      this.selectedGoalPresetId = this.trainNext?.goalSetup?.presets?.[1]?.id ?? this.trainNext?.goalSetup?.presets?.[0]?.id ?? null;
      this.customGoalDate = '';
    } catch (error) {
      LoggerService.error('Failed to load Train next:', error);
      this.trainNext = null;
    } finally {
      this.trainNextLoading = false;
    }
  }

  private async loadCoachReminder(): Promise<void> {
    this.coachReminderLoading = true;
    try {
      const sessionId = this.authService.getUser()?.sid;
      if (!sessionId) {
        this.coachReminder = null;
        return;
      }
      const courseId = this.currentCourseId;
      if (!courseId) {
        this.coachReminder = null;
        return;
      }
      this.coachReminder = await this.restService.getCoachReminder(sessionId, courseId);
    } catch (error) {
      LoggerService.error('Failed to load coach reminder:', error);
      this.coachReminder = null;
    } finally {
      this.coachReminderLoading = false;
    }
  }

  private async loadNewTestAccessState(): Promise<void> {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId) {
      this.canStartNewTest = false;
      this.hasExpiredTrial = false;
      this.newTestBlockedReason = 'Sign in to start a test.';
      return;
    }

    try {
      const [courses, subscriptions] = await Promise.all([
        this.restService.getCourses(sessionId),
        this.restService.getUserSubscriptions(sessionId),
      ]);
      if (user?.id) {
        void this.restService.refreshOfflineEntitlement(sessionId, user.id);
      }
      const usableCourseCount = courses?.length ?? 0;
      const hasExpiredTrialRecord = (subscriptions ?? []).some((subscription) => subscription.trialStatus === 'expired');
      const hasExpiredSubscription = (subscriptions ?? []).some((subscription) => {
        if (!subscription.validTo) return false;
        const validTo = Date.parse(
          subscription.validTo.length === 10
            ? `${subscription.validTo}T23:59:59.999Z`
            : subscription.validTo
        );
        return Number.isFinite(validTo) && validTo < Date.now();
      });
      this.hasExpiredTrial = usableCourseCount === 0 && hasExpiredTrialRecord;
      this.canStartNewTest = usableCourseCount > 0;
      if (this.canStartNewTest) {
        this.newTestBlockedReason = '';
      } else if (this.hasExpiredTrial) {
        this.newTestBlockedReason = 'Your trial has expired. Subscribe to start new tests.';
      } else if (!this.connectivity.isOnline && hasExpiredSubscription) {
        this.newTestBlockedReason = 'Your subscription has expired. Connect to verify access.';
      } else if (!this.connectivity.isOnline && !courses && !subscriptions) {
        this.newTestBlockedReason = 'Connect to verify your subscription before starting a new test.';
      } else {
        this.newTestBlockedReason = 'No active course is available. Subscribe to start new tests.';
      }
    } catch (error) {
      LoggerService.error('Failed to load New Test access state:', error);
      this.canStartNewTest = false;
      this.hasExpiredTrial = false;
      this.newTestBlockedReason = 'Course access could not be checked.';
    }
  }

  protected selectGoalPreset(presetId: string): void {
    this.selectedGoalPresetId = presetId;
    this.customGoalDate = '';
  }

  get currentCourseName(): string {
    return this.planningCourses.find((course) => course.id === this.currentCourseId)?.name ?? '';
  }

  protected async onDashboardCourseChange(courseId: number | string | null): Promise<void> {
    const id = Number(courseId);
    if (!Number.isFinite(id) || id <= 0 || id === this.currentCourseId) {
      return;
    }
    this.currentCourseId = id;
    this.restService.courseId = id;
    localStorage.setItem(this.brand.storageKey(DASHBOARD_COURSE_STORAGE_SUFFIX), String(id));
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    this.trainNextLoading = true;
    this.coachReminderLoading = true;
    try {
      this.trainNext = await this.restService.getTrainNextRecommendation(sessionId, id);
      this.selectedGoalPresetId = this.trainNext?.goalSetup?.presets?.[1]?.id ?? this.trainNext?.goalSetup?.presets?.[0]?.id ?? null;
      this.customGoalDate = '';
      await this.loadCoachReminder();
    } catch (error) {
      LoggerService.error('Failed to switch dashboard course:', error);
    } finally {
      this.trainNextLoading = false;
    }
  }

  protected onCustomGoalDateChange(): void {
    this.selectedGoalPresetId = null;
  }

  protected async saveSelectedGoal(): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId || !this.currentCourseId || !this.trainNext?.goalSetup) {
      return;
    }
    const preset = this.trainNext.goalSetup.presets.find(item => item.id === this.selectedGoalPresetId);
    const targetDate = this.customGoalDate || preset?.targetDate;
    if (!targetDate) {
      return;
    }
    this.goalSaving = true;
    try {
      const saved = await this.restService.saveCourseGoal(sessionId, {
        courseId: this.currentCourseId,
        targetDate,
      });
      if (saved) {
        await this.recordTrainNextAccepted();
        await this.loadTrainNext();
      }
    } finally {
      this.goalSaving = false;
    }
  }

  protected async openTrainNextTest(): Promise<void> {
    if (!this.trainNext) {
      return;
    }
    await this.recordTrainNextAccepted();
    this.restService.clearRecreateSettings();
    this.restService.setRecreateSettings(this.restService.buildTrainNextPrefill(this.trainNext));
    await this.router.navigate(['/question-pool']);
  }

  private async recordTrainNextAccepted(): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId || !this.trainNext) {
      return;
    }
    await this.restService.acceptTrainNextRecommendation(sessionId, this.trainNext);
  }

  private skyCoachContextCache?: SkyCoachContext;

  protected skyCoachContext(): SkyCoachContext {
    const next: SkyCoachContext = {
      surface: 'dashboard',
      isLoading: this.trainNextLoading || this.coachReminderLoading,
      hasExpiredTrial: this.hasExpiredTrial,
      canStartNewTest: this.canStartNewTest,
      recommendationType: this.trainNext?.recommendationType,
      readinessStatus: this.trainNext?.readiness?.status,
      remainingToday: this.trainNext?.dailyTarget?.remainingToday,
      onTrack: this.trainNext?.dailyTarget?.onTrack,
      streakDay: this.trainNext?.dailyTarget?.streakDay,
      reason: this.trainNext?.reason,
      subjectName: this.trainNext?.subjectName,
      dueReviews: this.trainNext?.readiness?.dueReviews,
      reminderTitle: this.coachReminder?.title,
      reminderBody: this.coachReminder?.body,
    };
    const prev = this.skyCoachContextCache;
    if (
      prev
      && prev.isLoading === next.isLoading
      && prev.hasExpiredTrial === next.hasExpiredTrial
      && prev.canStartNewTest === next.canStartNewTest
      && prev.recommendationType === next.recommendationType
      && prev.readinessStatus === next.readinessStatus
      && prev.remainingToday === next.remainingToday
      && prev.onTrack === next.onTrack
      && prev.streakDay === next.streakDay
      && prev.reason === next.reason
      && prev.subjectName === next.subjectName
      && prev.dueReviews === next.dueReviews
      && prev.reminderTitle === next.reminderTitle
      && prev.reminderBody === next.reminderBody
    ) {
      return prev;
    }
    this.skyCoachContextCache = next;
    return next;
  }

  private async checkContentPackUpdates(): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    const courses = await this.restService.getCourses(sessionId);
    if (!courses?.length) {
      return;
    }
    const courseIds = courses
      .map((c) => Number(c.courseId))
      .filter((id) => Number.isFinite(id) && id > 0);
    await this.contentPackService.refreshIfNeeded(courseIds);
  }

  private async loadNews() {
    this.newsLoading = true;
    try {
      const sessionId = this.authService.getUser()?.sid;
      if (!sessionId) {
        this.newsItems = [];
        return;
      }
      const list = await this.restService.getActiveNews(sessionId);
      this.newsItems = (list ?? []).slice(0, 3);
    } catch {
      this.newsItems = [];
    } finally {
      this.newsLoading = false;
    }
  }

  private async loadStatistics() {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        return;
      }

      const stats = await this.restService.getDashboardStatistics(sessionId);
      if (stats) {
        this.averageScore = stats.averageScore || 0;
        this.questionsSeen = stats.questionsSeen || 0;
        this.testsDone = stats.testsDone ?? 0;
        this.xpTotal = stats.xpTotal ?? 0;
        this.xpToday = stats.xpToday ?? 0;
        this.xpWeek = stats.xpWeek ?? 0;
        this.xpLevel = stats.xpLevel ?? 1;
        this.recentMilestones = stats.recentMilestones ?? [];
        this.nextMilestone = stats.nextMilestone ?? null;
        this.lastActiveCourseId = Number(stats.lastActiveCourseId) || null;
        this.milestoneBadgesService.setRecentMilestones(this.recentMilestones);
        
        // Initialize chart with real data
        this.initializeChartWithData(stats.testHistory || []);
      } else {
        // Fallback to defaults if API call fails
        this.averageScore = 0;
        this.questionsSeen = 0;
        this.testsDone = 0;
        this.xpTotal = 0;
        this.xpToday = 0;
        this.xpWeek = 0;
        this.xpLevel = 1;
        this.recentMilestones = [];
        this.nextMilestone = null;
        this.lastActiveCourseId = null;
        this.milestoneBadgesService.setRecentMilestones([]);
        this.initializeChartWithData([]);
      }
    } catch (error) {
      LoggerService.error('Failed to load dashboard statistics:', error);
      this.averageScore = 0;
      this.questionsSeen = 0;
      this.testsDone = 0;
      this.xpTotal = 0;
      this.xpToday = 0;
      this.xpWeek = 0;
      this.xpLevel = 1;
      this.recentMilestones = [];
      this.nextMilestone = null;
      this.lastActiveCourseId = null;
      this.milestoneBadgesService.setRecentMilestones([]);
      this.initializeChartWithData([]);
    }
  }

  protected formatMilestonePercent(value?: number | null): string {
    if (value == null || !Number.isFinite(value)) {
      return '0%';
    }
    return `${Math.round(value * 100)}%`;
  }

  protected nextMilestoneProgressLabel(milestone: DashboardMilestone): string {
    if (milestone.type === 'SUBJECT_FINISHED') {
      return `${this.formatMilestonePercent(milestone.progress)} ready`;
    }
    return `${this.formatMilestonePercent(milestone.progress)} / ${this.formatMilestonePercent(milestone.threshold)}`;
  }

  protected milestoneTitleLead(milestone: DashboardMilestone): string {
    const match = milestone.title.match(/^(\d+%)/);
    return match ? match[1] : '';
  }

  protected milestoneTitleRest(milestone: DashboardMilestone): string {
    const match = milestone.title.match(/^\d+%\s*(.*)/);
    return match?.[1] ? ` ${match[1]}` : '';
  }

  protected isCoverageMilestone(milestone: DashboardMilestone): boolean {
    return milestone.type === 'SUBJECT_COVERAGE';
  }

  protected milestoneCoverageTooltip(milestone: DashboardMilestone): string {
    const subject = milestone.subjectName || 'this subject';
    const current = this.formatMilestonePercent(milestone.progress);
    const target = this.formatMilestonePercent(milestone.threshold);
    const xp = milestone.xpAwarded ?? 0;
    return `Progress toward your next badge in ${subject}. You have seen ${current} of active questions; reach ${target} to earn +${xp} XP. This tracks unique questions seen, not your test score.`;
  }

  protected usesTapCoverageHelp(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(hover: none), (pointer: coarse)').matches
      || window.matchMedia('(max-width: 480px)').matches
      || document.body.classList.contains('platform-native');
  }

  protected onCoverageHelpClick(event: Event): void {
    event.stopPropagation();
    if (!this.usesTapCoverageHelp()) {
      return;
    }
    this.coverageHelpOpen = !this.coverageHelpOpen;
  }

  @HostListener('document:click')
  protected closeCoverageHelp(): void {
    this.coverageHelpOpen = false;
  }

  protected nextMilestoneProgressPercent(milestone: DashboardMilestone): number {
    const progress = milestone.progress ?? 0;
    if (milestone.type === 'SUBJECT_FINISHED') {
      return Math.min(100, Math.round(progress * 100));
    }
    const threshold = milestone.threshold ?? 0;
    if (threshold <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((progress / threshold) * 100));
  }

  private initializeChartWithData(testHistory: TestPerformance[]) {
    // Store test history for tooltip access
    this.testHistory = testHistory || [];
    this.chartHasData = this.testHistory.length > 0;
    
    // Create a map for quick lookup: "timestamp-mode" -> testData
    this.testDataMap.clear();
    (testHistory || []).forEach(d => {
      const timestamp = new Date(d.date).getTime();
      const key = `${timestamp}-${d.mode}`;
      this.testDataMap.set(key, d);
    });

    // Group by mode instead of subject
    // Handle both "test" and "study" as study mode
    const normalizedHistory = (testHistory || []).map(d => ({
      ...d,
      normalizedMode: d.mode === 'exam' ? 'exam' : 'test' // Normalize to 'test' for study mode
    }));
    
    const modes = [...new Set(normalizedHistory.map(d => d.normalizedMode))];

    const series = modes.map(mode => {
      const filteredData = normalizedHistory.filter(d => d.normalizedMode === mode);
      return {
        name: mode === 'exam' ? 'Exam' : 'Study',
        data: filteredData.map(d => {
          const timestamp = new Date(d.date).getTime();
          return {
            x: timestamp,
            y: d.score,
            questions: d.questionCount,
            date: d.date,
            subject: d.subjectName,
            mode: d.mode, // Keep original mode for matching
            normalizedMode: d.normalizedMode
          };
        })
      };
    });

    // Mobile viewports get a shorter chart so the dashboard above-the-fold
    // shows stats, chart and main functions without scrolling forever.
    // ApexCharts paints the SVG at this exact height, so CSS clamps don't apply.
    const isMobileViewport =
      typeof window !== 'undefined' && window.matchMedia &&
      (window.matchMedia('(max-width: 480px)').matches || document.body.classList.contains('platform-native'));
    const chartHeight = isMobileViewport ? 220 : 280;

    this.chartOptions = {
      series: series,
      chart: {
        type: 'line',
        height: chartHeight,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM dd'
        }
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 5,
        title: {
          text: 'Score (%)',
          style: {
            fontSize: '18px',
            fontWeight: 600
          }
        },
        labels: {
          style: {
            fontSize: '15px'
          },
          formatter: (value: number) => {
            return Math.round(value).toString();
          }
        }
      },
      tooltip: {
        enabled: true,
        shared: false,
        intersect: true,
        custom: (({ seriesIndex, dataPointIndex, w }: any) => {
          try {
            if (seriesIndex === undefined || dataPointIndex === undefined || !w || !w.globals) {
              return '';
            }
            
            const seriesName = w.globals.seriesNames[seriesIndex];
            const yValue = w.globals.series[seriesIndex][dataPointIndex];
            const xValue = w.globals.seriesX[seriesIndex][dataPointIndex];
            
            // Determine the mode based on series name
            const modeToMatch = seriesName === 'Exam' ? 'exam' : 'test';
            
            // Try to find test data using the map (most reliable)
            let testData = null;
            const key = `${xValue}-${modeToMatch}`;
            testData = this.testDataMap.get(key);
            
            // If not found with exact match, try with tolerance for timestamp
            if (!testData) {
              // Try all modes for this timestamp
              for (const [mapKey, value] of this.testDataMap.entries()) {
                const [mapTimestamp] = mapKey.split('-');
                if (Math.abs(parseInt(mapTimestamp) - xValue) < 1000) {
                  // Check if mode matches (for exam) or is test/study (for study)
                  if ((seriesName === 'Exam' && value.mode === 'exam') ||
                      (seriesName === 'Study' && (value.mode === 'test' || value.mode === 'study'))) {
                    testData = value;
                    break;
                  }
                }
              }
            }
            
            // If still not found, try simple search
            if (!testData && this.testHistory && this.testHistory.length > 0) {
              testData = this.testHistory.find(d => {
                const dateMatch = Math.abs(new Date(d.date).getTime() - xValue) < 1000;
                const modeMatch = (seriesName === 'Exam' && d.mode === 'exam') ||
                                 (seriesName === 'Study' && (d.mode === 'test' || d.mode === 'study'));
                return dateMatch && modeMatch;
              });
            }
            
            if (testData) {
              return `
                <div style="padding: 10px; background: white; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                  <strong>Subject: ${testData.subjectName}</strong><br/>
                  Score: ${Math.round(testData.score)}%<br/>
                  Questions: ${testData.questionCount}
                </div>
              `;
            }
            
            // Last resort fallback
            return `<div style="padding: 5px;">${seriesName}: ${Math.round(yValue)}%</div>`;
          } catch (error) {
            console.error('Tooltip error:', error);
            // Return a safe fallback
            return '<div style="padding: 5px;">Error loading tooltip</div>';
          }
        }).bind(this)
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        floating: false,
        fontSize: '14px',
        fontFamily: 'Helvetica, Arial',
        fontWeight: 400,
        formatter: undefined,
        inverseOrder: false,
        width: undefined,
        height: undefined,
        tooltipHoverFormatter: undefined,
        customLegendItems: [],
        offsetX: 0,
        offsetY: 0,
        labels: {
          colors: undefined,
          useSeriesColors: true
        },
        markers: {
          width: 12,
          height: 12,
          strokeWidth: 0,
          strokeColor: '#fff',
          fillColors: undefined,
          radius: 12,
          customHTML: undefined,
          onClick: undefined,
          offsetX: 0,
          offsetY: 0
        },
        itemMargin: {
          horizontal: 5,
          vertical: 0
        },
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        row: {
          colors: ['transparent', 'transparent'],
          opacity: 0
        }
      },
      markers: {
        size: 5,
        hover: {
          size: 7
        }
      }
    };
  }

  private async loadFeatureFlags(): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    await this.retentionFlags.ensureLoaded(sessionId);
  }

  private async loadSocialSummary(): Promise<void> {
    if (!this.retentionFlags.studyTogetherEnabled()) {
      this.socialBadgeCount = 0;
      return;
    }
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      this.socialBadgeCount = 0;
      return;
    }
    const summary = await this.restService.getSocialSummary(sessionId);
    const pending = summary?.pendingFriendRequests ?? 0;
    const shared = summary?.sharedTestsCount ?? 0;
    this.socialBadgeCount = pending + shared;
  }

  protected leaderboardRankLabel(): string {
    if (this.leaderboard?.optedIn && this.leaderboard.viewerRank) {
      return `#${this.leaderboard.viewerRank} this week`;
    }
    return 'Weekly rank';
  }

  protected async openLeaderboardModal(force = false): Promise<void> {
    if (!this.retentionFlags.leaderboardEnabled()) {
      return;
    }
    const sessionId = this.authService.getUser()?.sid;
    const courseId = this.currentCourseId;
    if (!sessionId || !courseId) {
      return;
    }
    this.leaderboardLoading = true;
    this.leaderboardOpen = true;
    try {
      this.leaderboard = await this.restService.getLeaderboard(sessionId, courseId);
      if (!force && this.leaderboard?.climb) {
        this.markLeaderboardShown();
      }
    } finally {
      this.leaderboardLoading = false;
    }
  }

  protected closeLeaderboardModal(): void {
    this.leaderboardOpen = false;
  }

  private async handleLeaderboardTrigger(): Promise<void> {
    const trigger = this.route.snapshot.queryParamMap.get('leaderboard');
    if (trigger === 'panel' || trigger === 'climb') {
      await this.openLeaderboardModal(true);
      return;
    }
    if (this.shouldAutoOpenLeaderboard()) {
      const sessionId = this.authService.getUser()?.sid;
      const courseId = this.currentCourseId;
      if (!sessionId || !courseId) {
        return;
      }
      const board = await this.restService.getLeaderboard(sessionId, courseId);
      if (board?.climb) {
        this.leaderboard = board;
        this.leaderboardOpen = true;
        this.markLeaderboardShown();
      }
    }
  }

  private shouldAutoOpenLeaderboard(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    const weekKey = this.currentWeekKey();
    return localStorage.getItem(`${BRAND.storagePrefix}.leaderboard.lastShownWeek`) !== weekKey;
  }

  private markLeaderboardShown(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(`${BRAND.storagePrefix}.leaderboard.lastShownWeek`, this.currentWeekKey());
  }

  private currentWeekKey(): string {
    const now = new Date();
    const day = now.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    return monday.toISOString().slice(0, 10);
  }
}
